import React from "react";
import { styled } from "styled-components";
import { ConfigEditor } from "./useConfigEditor";
import { tokens } from "./tokens";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

/**
 * Edit / Review, pinned to the top of the tractor's page.
 *
 * The config record is long enough to scroll, and its footer scrolls with it —
 * so an operator who changed a field near the bottom had to hunt for the save
 * controls, and one who wanted to start editing had to scroll back up. This bar
 * sticks under the page header and states the same things throughout: whether
 * you are editing, how much is pending, whether the last save landed or the
 * record moved underneath you, and what you can do about it.
 *
 * It is the *only* place Edit / Discard / Review live while the record is in
 * view. The card used to carry a second Edit button 76px below this one and a
 * "Cancel edit" that meant the same as this bar's "Cancel" — two buttons for
 * one action read as two actions. The card keeps a footer copy of Review for
 * the bottom of a long form, with the same words as here.
 *
 * It renders from the same `useConfigEditor` session as the panel — one count,
 * shown twice, rather than two counts that can disagree.
 */
interface ConfigActionBarProps {
  editor: ConfigEditor;
  /** Name to say in the idle state, so the bar reads as being about a tractor. */
  vehicleLabel: string;
}

const clock = (at: number) =>
  new Date(at).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

export const reviewLabel = (n: number) =>
  n === 0
    ? "Review changes"
    : n === 1
      ? "Review 1 change"
      : `Review ${n} changes`;

const ConfigActionBar: React.FC<ConfigActionBarProps> = ({
  editor,
  vehicleLabel,
}) => {
  const {
    editing,
    pendingCount,
    hasErrors,
    blocked,
    canEdit,
    lastSaved,
    conflictFields,
    saveError,
    reviewing,
  } = editor;

  return (
    <Bar
      $editing={editing}
      $tone={
        editing && (conflictFields !== null || (saveError && !reviewing))
          ? "warn"
          : undefined
      }
      aria-label="Config actions"
    >
      <Status>
        {editing ? (
          <>
            <strong>Editing {vehicleLabel}</strong>
            {pendingCount > 0 ? (
              <Badge tone="count">{pendingCount} pending</Badge>
            ) : (
              <Muted>no changes yet</Muted>
            )}
            {hasErrors && <Badge tone="bad">fix errors to review</Badge>}
            {/* The banner with the detail sits at the top of the card, which
                is off-screen for anyone who saved from down here. The bar
                says that something happened; the card says what. */}
            {conflictFields !== null && (
              <Badge tone="warn" role="status">
                changed by someone else — review again
              </Badge>
            )}
            {saveError && !reviewing && (
              <Badge tone="bad" role="alert">
                not saved
              </Badge>
            )}
          </>
        ) : lastSaved ? (
          <Saved role="status">
            <strong>
              Saved {lastSaved.count} change{lastSaved.count === 1 ? "" : "s"}
            </strong>
            <Muted>
              {lastSaved.version !== null && `now v${lastSaved.version} · `}
              {clock(lastSaved.at)}
            </Muted>
          </Saved>
        ) : (
          <Muted>
            {blocked ?? "Edits are saved together as one recorded change."}
          </Muted>
        )}
      </Status>

      {editing ? (
        <>
          <Button variant="ghost" size="sm" onClick={editor.requestCancel}>
            Discard changes
          </Button>
          <Button
            size="sm"
            onClick={editor.openReview}
            disabled={pendingCount === 0 || hasErrors}
          >
            {reviewLabel(pendingCount)}
          </Button>
        </>
      ) : (
        canEdit && (
          <Button size="sm" onClick={editor.start}>
            Edit
          </Button>
        )
      )}
    </Bar>
  );
};

const Bar = styled.div<{ $editing: boolean; $tone?: "warn" }>`
  position: sticky;
  /* Clears the page header, which is the scroll container's own top. */
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid
    ${(p) =>
      p.$tone === "warn"
        ? "rgba(255,150,0,0.5)"
        : p.$editing
          ? "rgba(0,105,255,0.35)"
          : tokens.line};
  /* Opaque: content scrolls underneath it. */
  background: ${(p) =>
    p.$tone === "warn" ? "#FFF7EA" : p.$editing ? "#F2F7FF" : tokens.white};
  box-shadow: ${tokens.shadowSm};
`;

const Status = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: ${tokens.ink};
`;

const Saved = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  color: ${tokens.readyText};
`;

const Muted = styled.span`
  font-size: 12.5px;
  color: ${tokens.slate};
`;

export default ConfigActionBar;
